// react
import React, { useEffect, useState } from 'react'
import { Switch, useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import withClearCache from './store/connector/withClearCache'
// routes
import { routes } from './store/routes'
import { authService } from './store/services/ServiceAuth'
// others
import './store/i18n'
import { initGA } from './index'

import CookieConsent from 'react-cookie-consent'
import { COOKIE_EXPIRATION, CONSENT_COOKIE } from './store/config'
import { grantAnalyticsConsent, declineAnalyticsConsent } from './store/helpers/trackingConsent'

// components
import PublicRoute from './components/routes/PublicRoute'
import PrivateRoute from './components/routes/PrivateRoute'
import ScrollToTop from './components/routes/ScrollToTop'
// styles
import './styles/fonts/style.css'
import './styles/styles.css'
import 'semantic-ui-css/semantic.min.css'
import './styles/default.css'
import './styles/dashboard.css'
import './styles/public.css'
import './styles/footer.css'
import './styles/navbar.css'
import './styles/activitydetail.css'

// pages
// eslint-disable-next-line
import Home from './pages/Home'
import Contact from './pages/contact/Contact'
import AboutUs from './pages/about-us/AboutUs'
import GeneralConditions from './pages/general-conditions/GeneralConditions'
import Privacy from './pages/privacy/Privacy'
import Error from './pages/Error'
import Login from './pages/login/Login'
import Register from './pages/register/Register'
import Dashboard from './pages/dashboard/Dashboard'
import Tutorial from './pages/tutorial/Tutorial'
import Activation from './pages/activation/Activation'
import Search from './pages/search/Search'
import Basket from './pages/payment-overview/Basket'
import PaymentFinish from './pages/payment-overview/PaymentFinish'
import Activity from './pages/activity/Activity'
import EventDetail from './pages/event-detail/EventDetail'
import ResetPassword from './pages/reset-password/ResetPassword'
import ConfirmResetPassword from './pages/reset-password/ConfirmResetPassword'
import APSolidarity from './pages/apsolidarity/APSolidarity'
import APBusiness from './pages/apbusiness/APBusiness'
import APEvent from './pages/apevent/APEvent'
import APwalkin from './pages/APwalkin'

import SuperModal from './components/modals/SuperModal'
import CookieSettings from './components/CookieSettings'
import APOthers from './pages/apothers/APOthers'
import ToastContainer from './components/others/ToastContainer'

function App() {
    const { i18n } = useTranslation()
    const dispatch = useDispatch()
    const history = useHistory()
    const language = useSelector((state) => state.language)
    const [initLang] = useState(language)
    const [googleReady, setGoogleReady] = useState(() => {
        return !new URLSearchParams(window.location.search).get('google_code')
    })

    useEffect(() => {
        i18n.changeLanguage(initLang)
    }, [i18n, initLang])

    useEffect(() => {
        initGA()
    }, [])

    useEffect(() => {
        const params = new URLSearchParams(window.location.search)
        const code = params.get('google_code')
        if (!code) {
            return
        }
        let cancelled = false

        async function finishGoogleLogin() {
            const result = await authService.completeGoogleLogin(code, dispatch, i18n)
            if (cancelled) {
                return
            }
            if (!result || result.status !== 200) {
                const errorCode = (result && result.response && result.response.error) || 'token_invalid'
                history.replace(routes.LOGIN + '?google_error=' + encodeURIComponent(errorCode))
            } else {
                params.delete('google_code')
                const search = params.toString()
                history.replace({
                    pathname: window.location.pathname,
                    search: search ? '?' + search : '',
                })
            }
            setGoogleReady(true)
        }

        finishGoogleLogin()
        return () => {
            cancelled = true
        }
    }, [dispatch, history, i18n])

    if (!googleReady) {
        return null
    }

    if (window.location.pathname.startsWith("/api") || window.location.pathname.startsWith("/admin")) {
        return null
    }

    return (
        <>
            {/* <InstallPWA/> */}
            <ScrollToTop />
            <Switch>
                <PublicRoute restricted={false} exact path={routes.HOME} component={Home} />
                <PublicRoute restricted={false} exact path={routes.CONTACT} component={Contact} />
                <PublicRoute restricted={false} exact path={routes.ABOUT_US} component={AboutUs} />
                <PublicRoute restricted={false} exact path={routes.GENERAL_CONDITIONS} component={GeneralConditions} />
                <PublicRoute restricted={false} exact path={routes.PRIVACY} component={Privacy} />

                <PublicRoute restricted={true} exact path={routes.LOGIN} component={Login} />
                <PublicRoute restricted={false} exact path={routes.PASSWORD_RESET} component={ResetPassword} />
                <PrivateRoute path={routes.DASHBOARD} component={Dashboard} />
                <PublicRoute restricted={true} path={routes.REGISTER} component={Register} />
                <PrivateRoute exact path={routes.TUTORIAL} component={Tutorial} />
                <PublicRoute restricted={false} exact path={routes.SEARCH} component={Search} />
                <PublicRoute restricted={false} exact path={routes.ACTIVITY_DETAIL} component={Activity} />

                <PublicRoute restricted={false} exact path={routes.ACTIVIY_EVENT_DETAIL} component={EventDetail} />
                <PublicRoute restricted={false} exact path={routes.BASKET} component={Basket} />
                <PublicRoute restricted={false} exact path={routes.PAYMENT_FINISH} component={PaymentFinish} />
                <PublicRoute restricted={false} exact path={routes.ACTIVATION} component={Activation} />
                <PublicRoute restricted={false} exact path={routes.PASSWORD_CONFIRM} component={ConfirmResetPassword} />
                <PublicRoute restricted={false} exact path={routes.AP_SOLIDARITY} component={APSolidarity} />
                <PublicRoute restricted={false} exact path={routes.AP_BUSINESS} component={APBusiness} />

                <PublicRoute restricted={false} exact path={routes.AP_ACTION} component={APwalkin} />

                <PublicRoute restricted={false} exact path={routes.AP_OTHERS} component={APOthers} />

                <PublicRoute restricted={false} exact path={routes.AP_EVENT} component={APEvent} />

                <PublicRoute restricted={false} exact path={routes.ALL}>
                    <Error code={'404'} message={'Stránka nenájdená'} />
                </PublicRoute>
            </Switch>
            <CookieConsent
                location="bottom"
                buttonText="Súhlasím"
                enableDeclineButton
                declineButtonText="Odmietnuť"
                flipButtons
                cookieName={CONSENT_COOKIE}
                onAccept={() => {
                    grantAnalyticsConsent()
                    initGA()
                }}
                onDecline={() => {
                    declineAnalyticsConsent()
                }}
                style={styles.cookie_container}
                buttonStyle={styles.cookie_button_style}
                declineButtonStyle={styles.cookie_decline_style}
                contentStyle={styles.cookie_content}
                expires={COOKIE_EXPIRATION}
            >
                <div style={{ fontFamily: 'Metropolis Regular' }}>
                    <strong style={{ display: 'block', fontSize: '1rem', marginBottom: '0.3rem', color: '#222' }}>
                        Vážime si vaše súkromie
                    </strong>
                    <span style={{ fontSize: '0.9rem', color: '#555', lineHeight: 1.5 }}>
                        Používame technické a analytické súbory cookie, aby sme zlepšovali fungovanie stránok a anonymne merali ich používanie. Súhlas môžete kedykoľvek odvolať v{' '}
                        <SuperModal
                            size="small"
                            centered={false}
                            closeIcon
                            trigger={
                                <span style={{ color: 'var(--primary)', fontWeight: 'bold', cursor: 'pointer', textDecoration: 'underline' }}>
                                    nastaveniach cookies
                                </span>
                            }
                            content={<CookieSettings />}
                        />.
                    </span>
                </div>
            </CookieConsent>
            <ToastContainer />
        </>
    )
}

const styles = {
    cookie_button_style: {
        background: 'var(--primary)',
        color: 'var(--white)',
        fontSize: 15,
        fontWeight: 'bold',
        padding: '0.8rem 2.2rem',
        borderRadius: '6px',
        margin: '0.3rem 0.6rem',
    },
    cookie_decline_style: {
        background: '#eceef1',
        color: '#444',
        fontSize: 15,
        fontWeight: 'bold',
        padding: '0.8rem 1.8rem',
        borderRadius: '6px',
        border: '1px solid #c5c9d0',
        margin: '0.3rem 0.6rem',
    },
    cookie_container: {
        background: '#ffffff',
        color: '#333',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '1.25rem',
        padding: '1.1rem 1.5rem',
        boxShadow: '0 -2px 16px rgba(0,0,0,0.12)',
        borderTop: '3px solid var(--primary)',
    },
    cookie_content: {
        flex: '1 1 460px',
        maxWidth: '760px',
        margin: 0,
    },
}
export default withClearCache(App)
